import { useState, useEffect, useRef } from "react";
import { ALPHABET, ContactAvatar, Icon, NET_BADGE, NetworkBadge, PROVIDERS, api, detectNetworkClient, paymentCallbackUrl } from "../lib/core";
import { RechargeConfirmationModal, RechargePaymentModal } from "../components/vend";

export const PRESET_TAGS = ["Family", "Office", "Friends", "Business"];

// Inline tag editor: preset chips + free-form add
export const TagEditor = ({ tags = [], onChange }) => {
  const [custom, setCustom] = useState("");
  const toggle = (t) => onChange(tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t]);
  const addCustom = () => {
    const v = custom.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setCustom("");
  };
  return (
    <div data-testid="tag-editor">
      <div className="flex flex-wrap gap-2">
        {[...new Set([...PRESET_TAGS, ...tags])].map((t) => (
          <button key={t} type="button" onClick={() => toggle(t)} data-testid={`tag-chip-${t}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all ${tags.includes(t) ? "border-primary-custom bg-primary-custom/5 text-primary-custom" : "border-slate-200 text-slate-500"}`}>
            {t}{tags.includes(t) && <span className="ml-1">✓</span>}
          </button>
        ))}
      </div>
      <div className="flex gap-2 mt-2">
        <input value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
          placeholder="Add a custom tag" data-testid="tag-custom-input"
          className="flex-1 h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom outline-none text-sm" />
        <button type="button" onClick={addCustom} data-testid="tag-custom-add" className="px-3 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm hover:bg-slate-200">Add</button>
      </div>
    </div>
  );
};

export const AddContactModal = ({ onClose, onSaved, initialPhone = "" }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState(initialPhone);
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const detected = detectNetworkClient(phone);

  const save = async () => {
    if (!name.trim() || !phone.trim()) { setError("Name and phone are required"); return; }
    setSaving(true); setError("");
    try {
      const res = await api.post("/contacts", { name: name.trim(), phone: phone.trim(), tags });
      onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save contact");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="add-contact-modal">
      <div className="bg-white rounded-3xl w-full max-w-md p-6 animate-fadeIn max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-900 text-lg">Add Contact</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="add-contact-close"><Icon name="close" className="text-slate-500" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-500">Name</label>
            <input data-testid="add-contact-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ade Okafor"
              className="w-full mt-1 h-12 px-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom outline-none" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Phone Number</label>
            <input data-testid="add-contact-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 0812 345 6789"
              className="w-full mt-1 h-12 px-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom outline-none" />
            {detected && <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1.5">Detected network: <NetworkBadge provider={detected} /></p>}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Tags <span className="text-slate-400 font-normal">(optional)</span></label>
            <div className="mt-1.5"><TagEditor tags={tags} onChange={setTags} /></div>
          </div>
          {error && <p className="text-sm text-red-600" data-testid="add-contact-error">{error}</p>}
          <button onClick={save} disabled={saving} data-testid="add-contact-save"
            className="w-full h-12 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold transition-all disabled:opacity-50">
            {saving ? "Saving..." : "Save Contact"}
          </button>
        </div>
      </div>
    </div>
  );
};


export const BulkTopupModal = ({ contacts = [], dataPlans = {}, walletBalance = 0, onClose, onDone }) => {
  const [list, setList] = useState(contacts);
  const [service, setService] = useState("airtime");
  const [amount, setAmount] = useState("");
  const [plan, setPlan] = useState(null);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  const providers = [...new Set(list.map((c) => c.provider).filter(Boolean))];
  const sameNetwork = providers.length === 1 ? providers[0] : null;
  const dataAllowed = service === "airtime" || !!sameNetwork;
  const perCharge = service === "data" ? (plan?.amount || 0) : (parseFloat(amount) || 0);
  const total = perCharge * list.length;
  const short = total > walletBalance;

  const remove = (id) => setList((l) => l.filter((c) => c.id !== id));

  const send = async () => {
    if (list.length === 0) return;
    if (service === "data" && !plan) { setResults({ error: "Select a data plan." }); return; }
    if (service === "airtime" && (!perCharge || perCharge <= 0)) { setResults({ error: "Enter a valid amount." }); return; }
    if (short) { setResults({ error: `Wallet short by ₦${(total - walletBalance).toLocaleString()}.` }); return; }
    setSending(true); setResults(null);
    try {
      const payload = { contact_ids: list.map((c) => c.id), service_type: service, amount: perCharge };
      if (service === "data" && plan) payload.subcode = plan.subCode;
      const res = await api.post("/contacts/bulk-recharge", payload);
      setResults(res.data);
      if (res.data.successful > 0 && onDone) onDone();
    } catch (err) {
      setResults({ error: err.response?.data?.detail || "Bulk top-up failed" });
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="bulk-topup-modal">
      <div className="bg-white rounded-3xl w-full max-w-md p-6 animate-fadeIn max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-900 text-lg">Top up {list.length} contact{list.length === 1 ? "" : "s"}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="bulk-close"><Icon name="close" className="text-slate-500" /></button>
        </div>

        {results ? (
          <div data-testid="bulk-results" className="space-y-3">
            {results.error ? (
              <div className="bg-red-50 text-red-600 rounded-xl p-3 text-sm flex items-center gap-2"><Icon name="error" />{results.error}</div>
            ) : (
              <>
                <div className="bg-slate-50 rounded-xl p-3 text-sm font-semibold text-slate-700">
                  {results.successful} succeeded · {results.failed} failed
                </div>
                <div className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
                  {results.results?.map((r) => (
                    <div key={r.contact_id} className="flex items-center justify-between py-2 text-sm">
                      <span className="truncate">{r.name} · {r.phone}</span>
                      <span className={`font-semibold flex items-center gap-1 ${r.status === "success" ? "text-green-600" : "text-red-600"}`}>
                        <Icon name={r.status === "success" ? "check_circle" : "error"} className="text-base" />{r.status === "success" ? "Sent" : "Failed"}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
            <button onClick={onClose} data-testid="bulk-done" className="w-full h-12 bg-primary-custom text-white rounded-xl font-bold">Done</button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {list.map((c) => (
                <span key={c.id} className="inline-flex items-center gap-1 bg-slate-100 rounded-full pl-3 pr-1 py-1 text-xs font-semibold text-slate-700" data-testid={`bulk-chip-${c.id}`}>
                  {c.name}
                  <button onClick={() => remove(c.id)} className="w-5 h-5 rounded-full hover:bg-slate-200 flex items-center justify-center" data-testid={`bulk-remove-${c.id}`}><Icon name="close" className="text-sm text-slate-400" /></button>
                </span>
              ))}
            </div>

            <div className="flex gap-2">
              {["airtime", "data"].map((s) => (
                <button key={s} onClick={() => { setService(s); setAmount(""); setPlan(null); setResults(null); }}
                  className={`flex-1 py-2 rounded-xl text-sm font-semibold capitalize ${service === s ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`bulk-service-${s}`}>{s}</button>
              ))}
            </div>

            {service === "airtime" ? (
              <div>
                <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount per contact (₦)"
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom outline-none text-center font-bold text-lg" data-testid="bulk-amount" />
                <div className="flex flex-wrap gap-2 mt-2">
                  {[100, 200, 500, 1000].map((a) => (
                    <button key={a} onClick={() => setAmount(a.toString())} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${amount === a.toString() ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"}`}>₦{a}</button>
                  ))}
                </div>
              </div>
            ) : dataAllowed ? (
              <select value={plan?.subCode || ""} onChange={(e) => setPlan((dataPlans[sameNetwork] || []).find((p) => p.subCode === e.target.value) || null)}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-slate-50 outline-none" data-testid="bulk-plan">
                <option value="">{(dataPlans[sameNetwork] || []).length ? `Select a ${sameNetwork} data plan` : "No plans for this network"}</option>
                {(dataPlans[sameNetwork] || []).map((pl, i) => (
                  <option key={`${pl.subCode}-${i}`} value={pl.subCode}>{pl.description || pl.subCode} — ₦{pl.amount?.toLocaleString?.() || pl.amount}</option>
                ))}
              </select>
            ) : (
              <div className="bg-amber-50 text-amber-700 rounded-xl p-3 text-sm" data-testid="bulk-data-note">
                Data top-ups need all selected contacts on the same network. Yours span {providers.join(", ")}. Use Airtime, or select one network.
              </div>
            )}

            <div className="flex items-center justify-between text-sm bg-slate-50 rounded-xl p-3">
              <span className="text-slate-500">Total ({list.length} × ₦{perCharge.toLocaleString()})</span>
              <span className={`font-bold ${short ? "text-red-600" : "text-slate-900"}`} data-testid="bulk-total">₦{total.toLocaleString()}</span>
            </div>
            {short && <p className="text-xs text-red-600" data-testid="bulk-short">Wallet ₦{walletBalance.toLocaleString()} — short by ₦{(total - walletBalance).toLocaleString()}.</p>}

            <button onClick={send} disabled={sending || list.length === 0} data-testid="bulk-send"
              className="w-full h-12 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50">
              {sending ? <><Icon name="progress_activity" className="animate-spin" /> Sending…</> : <><Icon name="bolt" /> Top up all</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};


export const ScheduledTopupsPage = ({ onBack, allTags = [], dataPlans = {} }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ tag: allTags[0] || "", service_type: "airtime", amount: "", provider: "MTN", subcode: "", frequency: "monthly", day_of_week: 1, day_of_month: 1, time_of_day: "09:00" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => { setLoading(true); api.get("/contacts/tag-schedules").then((r) => setSchedules(r.data.schedules || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async () => {
    setError("");
    if (!form.tag) { setError("Pick a tag"); return; }
    const amt = form.service_type === "data" ? (dataPlans[form.provider] || []).find((p) => p.subCode === form.subcode)?.amount : parseFloat(form.amount);
    if (!amt || amt <= 0) { setError(form.service_type === "data" ? "Select a data plan" : "Enter a valid amount"); return; }
    setSaving(true);
    try {
      const payload = {
        tag: form.tag, service_type: form.service_type, amount: amt,
        frequency: form.frequency, time_of_day: form.time_of_day,
        day_of_week: form.frequency === "weekly" ? Number(form.day_of_week) : null,
        day_of_month: form.frequency === "monthly" ? Number(form.day_of_month) : null,
      };
      if (form.service_type === "data") { payload.provider = form.provider; payload.subcode = form.subcode; }
      await api.post("/contacts/tag-schedules", payload);
      setShowForm(false); setForm((f) => ({ ...f, amount: "", subcode: "" })); load();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save schedule");
    } finally { setSaving(false); }
  };

  const toggle = async (s) => { await api.put(`/contacts/tag-schedules/${s.id}`, { is_active: !s.is_active }).catch(() => {}); load(); };
  const remove = async (s) => { if (!window.confirm(`Delete the ${s.tag} schedule?`)) return; await api.delete(`/contacts/tag-schedules/${s.id}`).catch(() => {}); load(); };

  const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const freqLabel = (s) => s.frequency === "daily" ? "Every day" : s.frequency === "weekly" ? `Every ${DOW[s.day_of_week ?? 0]}` : `Day ${s.day_of_month} monthly`;

  return (
    <div className="min-h-screen bg-background-light" data-testid="tag-schedules-page">
      <header className="bg-primary-custom text-white sticky top-0 z-50" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={onBack} data-testid="schedules-back" className="p-2 hover:bg-white/10 rounded-lg"><Icon name="arrow_back" /></button>
          <span className="font-bold text-xl">Scheduled Top-ups</span>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-4 pb-28">
        <p className="text-sm text-slate-500">Automatically top up everyone in a tag group on a schedule. Paid from your wallet; failed sends auto-refund.</p>

        {loading ? (
          <div className="text-center py-10 text-slate-400">Loading…</div>
        ) : schedules.length === 0 ? (
          <div className="text-center py-12 text-slate-400 bg-white rounded-2xl border border-slate-200/70" data-testid="schedules-empty">
            <Icon name="event_repeat" className="text-5xl" />
            <p className="mt-2 font-medium">No scheduled top-ups yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {schedules.map((s) => (
              <div key={s.id} className="bg-white rounded-2xl border border-slate-200/70 p-4" data-testid={`schedule-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 flex items-center gap-2"><span className="bg-primary-custom/10 text-primary-custom text-xs px-2 py-0.5 rounded-full">{s.tag}</span><span className="text-slate-400 text-xs">{s.contact_count} contact{s.contact_count === 1 ? "" : "s"}</span></p>
                    <p className="text-sm text-slate-600 mt-1 capitalize">{s.service_type} · ₦{Number(s.amount).toLocaleString()} each</p>
                    <p className="text-xs text-slate-400 mt-0.5">{freqLabel(s)} at {s.time_of_day} · next {s.next_execution ? new Date(s.next_execution).toLocaleDateString() : "—"}{s.last_status ? ` · last ${s.last_status}` : ""}</p>
                    {s.is_active && s.affordable === false && s.estimated_cost > 0 && (
                      <p className="text-xs text-amber-600 font-semibold mt-1 flex items-center gap-1" data-testid={`schedule-lowbal-${s.id}`}>
                        <Icon name="warning" className="text-sm" /> Needs ₦{Number(s.estimated_cost).toLocaleString()} — wallet may be short
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => toggle(s)} data-testid={`schedule-toggle-${s.id}`} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{s.is_active ? "Active" : "Paused"}</button>
                    <button onClick={() => remove(s)} data-testid={`schedule-delete-${s.id}`} className="p-1.5 text-slate-300 hover:text-red-600"><Icon name="delete" className="text-lg" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!showForm ? (
          <>
            <button onClick={() => setShowForm(true)} disabled={allTags.length === 0} data-testid="schedule-add"
              className="w-full h-12 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50">
              <Icon name="add" /> New scheduled top-up
            </button>
            {allTags.length === 0 && (
              <p className="text-center text-xs text-slate-400 mt-2" data-testid="schedule-no-tags">Tag a few contacts first (e.g. "Family"), then come back to schedule a group top-up.</p>
            )}
          </>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200/70 p-5 space-y-4" data-testid="schedule-form">
            <div>
              <label className="text-xs font-semibold text-slate-500">Tag group</label>
              <select value={form.tag} onChange={set("tag")} data-testid="schedule-tag" className="w-full mt-1 h-11 px-3 rounded-xl border border-slate-200 bg-slate-50">
                {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              {["airtime", "data"].map((s) => (
                <button key={s} onClick={() => setForm((f) => ({ ...f, service_type: s, amount: "", subcode: "" }))} className={`flex-1 py-2 rounded-xl text-sm font-semibold capitalize ${form.service_type === s ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`schedule-service-${s}`}>{s}</button>
              ))}
            </div>
            {form.service_type === "airtime" ? (
              <div>
                <label className="text-xs font-semibold text-slate-500">Amount per contact (₦)</label>
                <input type="number" value={form.amount} onChange={set("amount")} data-testid="schedule-amount" className="w-full mt-1 h-11 px-3 rounded-xl border border-slate-200 bg-slate-50" placeholder="e.g. 200" />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500">Network (only contacts on this network are charged)</label>
                  <select value={form.provider} onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value, subcode: "" }))} data-testid="schedule-provider" className="w-full mt-1 h-11 px-3 rounded-xl border border-slate-200 bg-slate-50">
                    {PROVIDERS.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Data plan</label>
                  <select value={form.subcode} onChange={set("subcode")} data-testid="schedule-plan" className="w-full mt-1 h-11 px-3 rounded-xl border border-slate-200 bg-slate-50">
                    <option value="">{(dataPlans[form.provider] || []).length ? "Select a plan" : "No plans for this network"}</option>
                    {(dataPlans[form.provider] || []).map((pl, i) => <option key={`${pl.subCode}-${i}`} value={pl.subCode}>{pl.description || pl.subCode} — ₦{pl.amount?.toLocaleString?.() || pl.amount}</option>)}
                  </select>
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-slate-500">Frequency</label>
              <div className="flex gap-2 mt-1">
                {["daily", "weekly", "monthly"].map((fr) => (
                  <button key={fr} onClick={() => setForm((f) => ({ ...f, frequency: fr }))} className={`flex-1 py-2 rounded-xl text-sm font-semibold capitalize ${form.frequency === fr ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`schedule-freq-${fr}`}>{fr}</button>
                ))}
              </div>
            </div>
            {form.frequency === "weekly" && (
              <div>
                <label className="text-xs font-semibold text-slate-500">Day of week</label>
                <select value={form.day_of_week} onChange={set("day_of_week")} data-testid="schedule-dow" className="w-full mt-1 h-11 px-3 rounded-xl border border-slate-200 bg-slate-50">
                  {DOW.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </div>
            )}
            {form.frequency === "monthly" && (
              <div>
                <label className="text-xs font-semibold text-slate-500">Day of month (1–28)</label>
                <input type="number" min="1" max="28" value={form.day_of_month} onChange={set("day_of_month")} data-testid="schedule-dom" className="w-full mt-1 h-11 px-3 rounded-xl border border-slate-200 bg-slate-50" />
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-slate-500">Time (WAT)</label>
              <input type="time" value={form.time_of_day} onChange={set("time_of_day")} data-testid="schedule-time" className="w-full mt-1 h-11 px-3 rounded-xl border border-slate-200 bg-slate-50" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="schedule-error">{error}</p>}
            <div className="flex gap-2">
              <button onClick={() => { setShowForm(false); setError(""); }} className="flex-1 h-11 rounded-xl border border-slate-200 text-slate-600 font-semibold">Cancel</button>
              <button onClick={create} disabled={saving} data-testid="schedule-save" className="flex-1 h-11 bg-primary-custom text-white rounded-xl font-bold disabled:opacity-50">{saving ? "Saving…" : "Create"}</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};


export const ContactsPage = ({ onBack, pickMode = false, onPick, walletBalance = 0, dataPlans = {}, onTransactionComplete }) => {
  const [contacts, setContacts] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // { name, phone, provider, contact_id? }
  const [service, setService] = useState("airtime");
  const [amount, setAmount] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [vending, setVending] = useState(false);
  const [result, setResult] = useState(null);
  const [editingPhone, setEditingPhone] = useState(false);
  const [flwPayment, setFlwPayment] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [tagFilter, setTagFilter] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkIds, setBulkIds] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [selTags, setSelTags] = useState([]);
  const [savingTags, setSavingTags] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const listRef = useRef(null);

  const syncSupported = typeof navigator !== "undefined" && navigator.contacts && navigator.contacts.select;

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([api.get("/contacts"), api.get("/contacts/recent")]);
      setContacts(c.data || []);
      setRecent(r.data || []);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const presets = [100, 200, 500, 1000, 2000];

  const selectContact = (c) => {
    setSelected({ name: c.name, phone: c.phone, provider: detectNetworkClient(c.phone) || c.provider || "MTN", contact_id: c.contact_id || c.id });
    setSelTags(c.tags || []);
    setService("airtime"); setAmount(""); setSelectedPlan(null); setEditingPhone(false); setResult(null);
  };

  const toggleBulk = (c) => setBulkIds((ids) => ids.includes(c.id) ? ids.filter((x) => x !== c.id) : [...ids, c.id]);

  const saveSelTags = async (next) => {
    setSelTags(next);
    if (!selected?.contact_id) return;
    setSavingTags(true);
    try {
      await api.put(`/contacts/${selected.contact_id}`, { tags: next });
      setContacts((cs) => cs.map((c) => c.id === selected.contact_id ? { ...c, tags: next } : c));
    } catch (e) { /* noop */ } finally { setSavingTags(false); }
  };

  // All tags across contacts
  const allTags = [...new Set(contacts.flatMap((c) => c.tags || []))].sort();

  const handleSync = async () => {
    if (!syncSupported) return;
    setSyncing(true);
    try {
      const picked = await navigator.contacts.select(["name", "tel"], { multiple: true });
      const payload = picked.map((p) => ({ name: (p.name && p.name[0]) || (p.tel && p.tel[0]) || "", phone: (p.tel && p.tel[0]) || "" })).filter((p) => p.phone);
      if (payload.length) {
        await api.post("/contacts/sync", { contacts: payload });
        await fetchAll();
      }
    } catch (e) { /* user cancelled or unsupported */ } finally { setSyncing(false); }
  };

  const handleContinue = async () => {
    if (!selected) return;
    const prov = selected.provider;
    if (!prov) { setResult({ status: "failed", text: "Select a network for this number." }); return; }
    if (pickMode) { onPick({ phone: selected.phone, provider: prov, service, name: selected.name, contact_id: selected.contact_id }); return; }
    const amt = service === "data" ? selectedPlan?.amount : parseFloat(amount);
    if (service === "data" && !selectedPlan) { setResult({ status: "failed", text: "Select a data plan." }); return; }
    if (!amt || amt <= 0) { setResult({ status: "failed", text: "Enter a valid amount." }); return; }
    setVending(true); setResult(null);
    try {
      if (walletBalance >= amt) {
        const payload = {
          customer_phone: selected.phone,
          amount: amt,
          provider: prov,
          service_type: service,
          use_wallet: true,
        };
        if (service === "data" && selectedPlan) payload.subcode = selectedPlan.subCode;
        const res = await api.post("/vend/wallet", payload);
        const code = res.data.status_code;
        const pending = code === "PENDING";
        const ok = code === "000" || code === "OK";
        setResult({ status: pending ? "pending" : ok ? "ok" : "failed", text: res.data.response_message });
        if (ok || pending) { if (onTransactionComplete) onTransactionComplete(true); fetchAll(); }
      } else {
        // Insufficient wallet → pay via Flutterwave (card / bank / USSD)
        const payload = { customer_phone: selected.phone, amount: amt, provider: prov, service_type: service, redirect_url: paymentCallbackUrl() };
        if (service === "data" && selectedPlan) payload.subcode = selectedPlan.subCode;
        const res = await api.post("/payments/flutterwave/recharge/initiate", payload);
        if (res.data.success) { setFlwPayment({ ...res.data, provider: prov, customer_phone: selected.phone }); }
      }
    } catch (err) {
      setResult({ status: "failed", text: err.response?.data?.detail || "Transaction failed" });
    } finally { setVending(false); }
  };

  const onFlwVerified = (vend, recharge) => {
    setFlwPayment(null);
    const code = vend.status_code;
    const pending = code === "PENDING";
    const ok = code === "000" || code === "OK";
    if (recharge) {
      setConfirm({ ...recharge, delivered: ok });
    } else {
      setResult({ status: pending ? "pending" : ok ? "ok" : "failed", text: vend.response_message });
    }
    if (ok || pending) { if (onTransactionComplete) onTransactionComplete(false, !!recharge); fetchAll(); }
  };

  // Filter + group
  const q = query.trim().toLowerCase();
  const filtered = contacts.filter((c) =>
    (!q || c.name.toLowerCase().includes(q) || (c.phone || "").includes(q)) &&
    (!tagFilter || (c.tags || []).includes(tagFilter))
  );
  const grouped = {};
  filtered.forEach((c) => {
    const letter = /[a-z]/i.test(c.name?.[0] || "") ? c.name[0].toUpperCase() : "#";
    (grouped[letter] = grouped[letter] || []).push(c);
  });
  const letters = Object.keys(grouped).sort();
  const queryIsNumber = /^\+?\d[\d\s-]{4,}$/.test(query.trim());

  const scrollToLetter = (letter) => {
    const el = document.getElementById(`contact-group-${letter}`);
    if (el && listRef.current) listRef.current.scrollTo({ top: el.offsetTop - listRef.current.offsetTop - 8, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background-light" data-testid="contacts-page">
      <div className="max-w-2xl mx-auto px-4 py-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={onBack} className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50" data-testid="contacts-back">
            <Icon name="arrow_back" className="text-slate-700" />
          </button>
          <h1 className="font-bold text-xl text-slate-900">{pickMode ? "Select Contact" : bulkMode ? "Bulk Top-up" : "Contacts"}</h1>
          <div className="flex items-center gap-1">
            {!pickMode && (
              <button onClick={() => { setBulkMode((v) => !v); setBulkIds([]); setSelected(null); }} data-testid="contacts-bulk-toggle"
                className={`flex items-center gap-1.5 font-semibold text-sm px-2.5 py-1.5 rounded-lg transition-colors ${bulkMode ? "bg-primary-custom text-white" : "text-primary-custom hover:bg-primary-custom/5"}`}>
                <Icon name={bulkMode ? "close" : "bolt"} className="text-lg" /> {bulkMode ? "Cancel" : "Bulk"}
              </button>
            )}
            {!pickMode && !bulkMode && (
              <button onClick={() => setShowSchedules(true)} data-testid="contacts-schedules" className="flex items-center gap-1.5 text-primary-custom font-semibold text-sm px-2 py-1.5 rounded-lg hover:bg-primary-custom/5">
                <Icon name="event_repeat" className="text-lg" />
              </button>
            )}
            {syncSupported && !bulkMode && (
              <button onClick={handleSync} disabled={syncing} className="flex items-center gap-1.5 text-primary-custom font-semibold text-sm px-2 py-1.5 rounded-lg hover:bg-primary-custom/5 disabled:opacity-50" data-testid="contacts-sync">
                <Icon name="group_add" className="text-lg" /> {syncing ? "..." : "Sync"}
              </button>
            )}
            {!bulkMode && (
              <button onClick={() => setShowAdd(true)} className="w-10 h-10 rounded-full bg-primary-custom/10 text-primary-custom flex items-center justify-center hover:bg-primary-custom/20" data-testid="contacts-add">
                <Icon name="add" />
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <Icon name="search" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="contacts-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search contacts or enter number..."
            className="w-full h-12 pl-12 pr-4 rounded-2xl border border-slate-200 bg-white focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/15 outline-none transition-all"
          />
        </div>

        {!pickMode && allTags.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1 mb-4" data-testid="contacts-tag-filters">
            <button onClick={() => setTagFilter(null)} data-testid="tag-filter-all"
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${!tagFilter ? "bg-primary-custom text-white" : "bg-white text-slate-600 border border-slate-200"}`}>All</button>
            {allTags.map((t) => (
              <button key={t} onClick={() => setTagFilter(t === tagFilter ? null : t)} data-testid={`tag-filter-${t}`}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${tagFilter === t ? "bg-primary-custom text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{t}</button>
            ))}
          </div>
        )}

        {queryIsNumber && (
          <button
            onClick={() => selectContact({ name: query.trim(), phone: query.trim(), provider: detectNetworkClient(query.trim()) })}
            className="w-full mb-4 flex items-center gap-3 p-3 rounded-2xl bg-white border border-dashed border-primary-custom/40 hover:bg-primary-custom/5 transition-all"
            data-testid="contacts-use-number"
          >
            <span className="w-10 h-10 rounded-full bg-primary-custom/10 text-primary-custom flex items-center justify-center"><Icon name="dialpad" /></span>
            <div className="text-left">
              <p className="font-semibold text-slate-900">Top up this number</p>
              <p className="text-sm text-slate-500">{query.trim()}{detectNetworkClient(query.trim()) ? ` · ${detectNetworkClient(query.trim())}` : ""}</p>
            </div>
          </button>
        )}

        {/* Recently Topped Up */}
        {!q && recent.length > 0 && (
          <div className="mb-6" data-testid="recent-topped-up">
            <p className="font-bold text-slate-900 mb-3">Recently Topped Up</p>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {recent.map((r, i) => (
                <button key={`${r.phone}-${i}`} onClick={() => selectContact(r)} className="flex flex-col items-center gap-1.5 w-16 shrink-0" data-testid={`recent-${i}`}>
                  <ContactAvatar name={r.name} color={r.avatar_color} size="lg" />
                  <span className="text-xs font-medium text-slate-700 truncate w-full text-center">{r.name.split(" ")[0] || r.name}</span>
                  {r.provider && <span className="text-[10px] text-slate-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: NET_BADGE[r.provider]?.dot || "#94a3b8" }} />{r.provider}</span>}
                </button>
              ))}
              <button onClick={() => setShowAdd(true)} className="flex flex-col items-center gap-1.5 w-16 shrink-0" data-testid="recent-more">
                <span className="w-14 h-14 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center"><Icon name="add" /></span>
                <span className="text-xs font-medium text-slate-500">Add</span>
              </button>
            </div>
          </div>
        )}

        {/* All Contacts */}
        <div className="flex items-center justify-between mb-2">
          <p className="font-bold text-slate-900">All Contacts</p>
          <p className="text-sm text-slate-400">{filtered.length} contact{filtered.length === 1 ? "" : "s"}</p>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">Loading contacts…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-14 text-slate-400 bg-white rounded-3xl border border-slate-200/70">
            <Icon name="contacts" className="text-5xl" />
            <p className="mt-2 font-medium">No contacts yet</p>
            <button onClick={() => setShowAdd(true)} className="mt-4 px-5 py-2.5 bg-primary-custom text-white rounded-full font-semibold" data-testid="contacts-empty-add">Add your first contact</button>
          </div>
        ) : (
          <div className="relative flex">
            <div ref={listRef} className="flex-1 max-h-[60vh] overflow-y-auto pr-6 -mr-6">
              {letters.map((letter) => (
                <div key={letter} id={`contact-group-${letter}`}>
                  <div className="sticky top-0 bg-background-light py-1 text-sm font-bold text-primary-custom">{letter}</div>
                  <div className="bg-white rounded-2xl border border-slate-200/70 divide-y divide-slate-100 mb-3">
                    {grouped[letter].map((c) => (
                      <button key={c.id} onClick={() => bulkMode ? toggleBulk(c) : selectContact(c)} className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors text-left" data-testid={`contact-row-${c.id}`}>
                        {bulkMode && (
                          <span className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${bulkIds.includes(c.id) ? "bg-primary-custom border-primary-custom" : "border-slate-300"}`} data-testid={`bulk-check-${c.id}`}>
                            {bulkIds.includes(c.id) && <Icon name="check" className="text-white text-sm" />}
                          </span>
                        )}
                        <ContactAvatar name={c.name} color={c.avatar_color} />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 truncate">{c.name}</p>
                          <p className="text-sm text-slate-500">{c.phone}</p>
                          {(c.tags || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {c.tags.slice(0, 3).map((t) => (
                                <span key={t} className="text-[10px] font-semibold bg-primary-custom/10 text-primary-custom px-1.5 py-0.5 rounded-full">{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <NetworkBadge provider={c.provider} />
                        {!bulkMode && <Icon name="chevron_right" className="text-slate-300" />}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {/* A-Z index */}
            <div className="hidden sm:flex flex-col items-center justify-start pl-1 select-none">
              {ALPHABET.map((l) => (
                <button key={l} onClick={() => scrollToLetter(l)} disabled={!grouped[l]} className={`text-[10px] leading-tight font-semibold ${grouped[l] ? "text-slate-500 hover:text-primary-custom" : "text-slate-300"}`} data-testid={`az-${l}`}>{l}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bulk action bar */}
      {bulkMode && bulkIds.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4" data-testid="bulk-action-bar">
          <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 flex items-center justify-between">
            <span className="font-semibold text-slate-700">{bulkIds.length} selected</span>
            <button onClick={() => setShowBulk(true)} data-testid="bulk-open"
              className="px-5 h-11 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold flex items-center gap-2">
              <Icon name="bolt" /> Top up {bulkIds.length}
            </button>
          </div>
        </div>
      )}

      {/* Selected bottom sheet */}
      {selected && (
        <div className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4" data-testid="contacts-selected-sheet">
          <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="flex items-center gap-2 font-bold text-primary-custom"><Icon name="check_circle" /> Selected</span>
              <button onClick={() => setSelected(null)} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="contacts-clear-selected"><Icon name="close" className="text-slate-400" /></button>
            </div>
            <div className="flex items-center gap-3">
              <ContactAvatar name={selected.name} color={selected.contact_id ? undefined : "#EF4444"} />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-900 truncate">{selected.name}</p>
                {editingPhone ? (
                  <input autoFocus value={selected.phone} onChange={(e) => setSelected({ ...selected, phone: e.target.value, provider: detectNetworkClient(e.target.value) || selected.provider })}
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1 mt-0.5 outline-none focus:border-primary-custom" data-testid="selected-phone-edit" />
                ) : (
                  <p className="text-sm text-slate-500">{selected.phone}</p>
                )}
              </div>
              <button onClick={() => setEditingPhone((v) => !v)} className="p-2 text-primary-custom hover:bg-primary-custom/5 rounded-lg" data-testid="selected-edit"><Icon name="edit" className="text-lg" /></button>
            </div>

            {/* Network pills */}
            <div className="flex flex-wrap gap-2 mt-4">
              {PROVIDERS.map((p) => (
                <button key={p.name} onClick={() => setSelected({ ...selected, provider: p.name })}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all ${selected.provider === p.name ? "border-primary-custom bg-primary-custom/5 text-primary-custom" : "border-slate-200 text-slate-600"}`}
                  data-testid={`selected-net-${p.name}`}>
                  {p.name}
                </button>
              ))}
            </div>

            {!pickMode && selected.contact_id && (
              <div className="mt-4" data-testid="selected-tags">
                <p className="text-xs font-semibold text-slate-500 mb-1.5">Tags {savingTags && <span className="text-slate-400">· saving…</span>}</p>
                <TagEditor tags={selTags} onChange={saveSelTags} />
              </div>
            )}

            {!pickMode && (
              <>
                {/* Service toggle */}
                <div className="flex gap-2 mt-3">
                  {["airtime", "data"].map((s) => (
                    <button key={s} onClick={() => { setService(s); setAmount(""); setSelectedPlan(null); }}
                      className={`flex-1 py-2 rounded-xl text-sm font-semibold capitalize transition-all ${service === s ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-600"}`}
                      data-testid={`selected-service-${s}`}>
                      {s}
                    </button>
                  ))}
                </div>

                {service === "airtime" ? (
                  <div className="mt-3">
                    <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter amount"
                      className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom outline-none text-lg font-bold text-center" data-testid="selected-amount" />
                    <div className="flex flex-wrap gap-2 mt-2">
                      {presets.map((amt) => (
                        <button key={amt} onClick={() => setAmount(amt.toString())} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${amount === amt.toString() ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"}`} data-testid={`selected-preset-${amt}`}>₦{amt}</button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-3">
                    <select
                      value={selectedPlan?.subCode || ""}
                      onChange={(e) => {
                        const plan = (dataPlans[selected.provider] || []).find((pl) => pl.subCode === e.target.value);
                        setSelectedPlan(plan || null);
                      }}
                      className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary-custom outline-none" data-testid="selected-plan">
                      <option value="">{(dataPlans[selected.provider] || []).length ? "Select a data plan" : "No plans for this network"}</option>
                      {(dataPlans[selected.provider] || []).map((pl, idx) => (
                        <option key={`${pl.subCode}-${pl.amount}-${idx}`} value={pl.subCode}>{(pl.description || pl.name || pl.title || pl.plan || pl.subCode)} — ₦{pl.amount?.toLocaleString?.() || pl.amount}</option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}

            {result && (
              <div className={`mt-3 text-sm px-3 py-2 rounded-lg flex items-center gap-2 ${result.status === "ok" ? "bg-green-50 text-green-700" : result.status === "pending" ? "bg-yellow-50 text-yellow-700" : "bg-red-50 text-red-600"}`} data-testid="contacts-result">
                <Icon name={result.status === "ok" ? "check_circle" : result.status === "pending" ? "schedule" : "error"} className="text-base" />
                <span>{result.status === "pending" ? `Processing… ${result.text}` : result.text}</span>
              </div>
            )}

            <button onClick={handleContinue} disabled={vending}
              className="mt-4 w-full h-12 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              data-testid="contacts-continue">
              {vending ? <><Icon name="progress_activity" className="animate-spin" /> Processing…</> : <><Icon name="arrow_upward" /> {pickMode ? "Use this contact" : "Continue to Top-Up"}</>}
            </button>
          </div>
        </div>
      )}

      {showAdd && <AddContactModal onClose={() => setShowAdd(false)} onSaved={(c) => { setShowAdd(false); fetchAll(); selectContact(c); }} />}

      {flwPayment && (
        <RechargePaymentModal
          payment={flwPayment}
          serviceType={service}
          onClose={() => setFlwPayment(null)}
          onVerified={onFlwVerified}
        />
      )}

      {showBulk && (
        <BulkTopupModal
          contacts={contacts.filter((c) => bulkIds.includes(c.id)).map((c) => ({ id: c.id, name: c.name, phone: c.phone, provider: detectNetworkClient(c.phone) || c.provider }))}
          dataPlans={dataPlans}
          walletBalance={walletBalance}
          onClose={() => setShowBulk(false)}
          onDone={() => { if (onTransactionComplete) onTransactionComplete(true); setBulkIds([]); setBulkMode(false); fetchAll(); }}
        />
      )}

      {confirm && <RechargeConfirmationModal data={confirm} onClose={() => setConfirm(null)} />}

      {showSchedules && (
        <div className="fixed inset-0 z-[70] overflow-y-auto bg-background-light" data-testid="schedules-overlay">
          <ScheduledTopupsPage onBack={() => { setShowSchedules(false); }} allTags={allTags} dataPlans={dataPlans} />
        </div>
      )}
    </div>
  );
};




// ============================================================
// NEW DASHBOARD ARCHITECTURE — Sidebar + Main Content Layout
// ============================================================

// Sidebar navigation items
